import { z } from "zod";

/**
 * Пользователь. Ролей в схеме нет намеренно: «преподаватель» и «ученик» это
 * не свойство человека, а его позиция в конкретной паре и в конкретном уроке.
 * Один и тот же человек может учить одних и учиться у других.
 */
export const UserId = z.string().uuid();
export type UserId = z.infer<typeof UserId>;

export const DisplayName = z.string().min(1).max(80);
export type DisplayName = z.infer<typeof DisplayName>;

/**
 * Подпись над курсором на доске.
 *
 * Короче имени аккаунта вдвое (40 против 80), и это не придирка к базе: строка
 * висит над чужим курсором поверх рисунка, и всё, что длиннее пары слов,
 * закрывает собой то, на что этим курсором показывают.
 *
 * ⚠️ Пустая строка ДОПУСКАЕТСЯ и означает «убрать своё, вернуть как было»
 * (сервер кладёт NULL). Отдельной ручки «сбросить» поэтому нет, а `min(1)`
 * здесь стоять не может: он превратил бы очистку поля в `invalid_body`.
 */
export const BoardName = z.string().max(40);
export type BoardName = z.infer<typeof BoardName>;

export const CreateGuestRequest = z.object({
  displayName: DisplayName,
});
export type CreateGuestRequest = z.infer<typeof CreateGuestRequest>;

/**
 * Вход по логину и паролю.
 *
 * Логин это `provider_user_id` личности с провайдером `password`, поэтому его
 * длина ограничена той же сверху, что и в схеме БД (255): значение, которое не
 * влезет в колонку, обязано отваливаться на границе, а не на вставке.
 *
 * Верхняя граница пароля нужна не для строгости, а против отказа в
 * обслуживании: scrypt считает тем дольше, чем длиннее вход, и мегабайтное
 * поле превращает бесплатный запрос в секунды процессорного времени.
 * Нижняя граница здесь минимальная: требования к стойкости пароля это дело
 * того, кто его задаёт скриптом, а на входе слабый пароль должен получать
 * обычный отказ, а не подсказку «такой пароль слишком короткий».
 */
export const PasswordSignInRequest = z.object({
  login: z.string().min(1).max(255),
  password: z.string().min(1).max(1024),
});
export type PasswordSignInRequest = z.infer<typeof PasswordSignInRequest>;

/**
 * Нижняя граница длины НОВОГО пароля.
 *
 * ⚠️ Только для смены пароля из кабинета, ко входу она не относится вовсе:
 * там короткий пароль обязан получать обычное «не сошлось», а не подсказку о
 * длине (см. шапку `PasswordSignInRequest`). Скрипт `set-password` её тоже не
 * знает: он запускается руками тем, кто отвечает за стенд, а не человеком в
 * браузере.
 *
 * Восемь, и никаких требований к составу. Правила вида «цифра и заглавная»
 * дают предсказуемые `Password1!` и ничего больше; длина же работает всегда.
 * Значение живёт здесь, чтобы форма и сервер не разошлись: форма показывает
 * границу заранее, сервер отвечает `weak_password`, и оба берут одно число.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Смена своего пароля из кабинета.
 *
 * Текущий пароль обязателен и проверяется, хотя сессия и так открыта. Причина
 * не в формальности: открытая вкладка на чужом ноутбуке это самый частый
 * способ угона в продукте, где ученик заходит по ссылке из мессенджера, а
 * смена пароля без подтверждения превращает эту вкладку в захват аккаунта
 * навсегда.
 *
 * ⚠️ Нижняя граница у нового пароля здесь НЕ стоит, хотя проверка есть.
 * Схема отвечает `invalid_body`, а человеку нужно `weak_password`: разница
 * между «форма собрана неверно» и «пароль слишком короткий» это разница между
 * непонятной ошибкой и понятной. Длину проверяет маршрут, и он же нормализует
 * строку перед подсчётом.
 */
export const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1).max(1024),
  newPassword: z.string().min(1).max(1024),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

/**
 * `claimed` это «вход привязан», а не «аккаунт создан». Строка app_user есть
 * с первой секунды и у гостя тоже: при привязке входа уроки никуда не
 * переносятся, они уже его (раздел 5.10 CLAUDE.md).
 */
export const MeResponse = z.object({
  userId: UserId,
  displayName: DisplayName,
  claimed: z.boolean(),
  createdAt: z.number().int(),
  /** Когда протухнет сессия без активности. Скользящее продление двигает это вперёд. */
  sessionExpiresAt: z.number().int(),
  /**
   * Часовой пояс, имя IANA.
   *
   * ⚠️ `null` означает «человек ещё не подтверждал», а НЕ «UTC». Клиент по
   * этому полю решает, показать ли полосу «укажите часовой пояс», и молча
   * подставлять что-либо вместо него нельзя: московский преподаватель увидел
   * бы занятия на три часа раньше и не узнал бы об этом.
   *
   * Форму строки проверяет `TimeZoneName` из `./schedule.ts`; здесь схема
   * оставлена простой намеренно, чтобы этот файл не тянул за собой ничего:
   * `MeResponse` разбирает и старый бандл тоже.
   */
  timeZone: z.string().nullable().optional(),
  /**
   * С какой стороны у человека есть пары: есть ли у него ученики (`teaching`) и
   * есть ли преподаватели (`learning`).
   *
   * ⚠️ Это НЕ роль. Роли в схеме нет и не будет: роль это позиция в паре, а не
   * свойство человека, и один человек законно бывает обеими сторонами. Кабинет
   * читает эти два поля ровно затем, чтобы не показывать ученику разделы,
   * которые у него всегда пусты и ручки которых отвечают ему 404.
   *
   * ⚠️ Оба необязательны, и это не забывчивость: вкладка со старым бандлом их
   * не пришлёт и не разберёт, а новый бандл на старом API их не получит. В обоих
   * случаях верное поведение это «считаем преподавателем», то есть ровно то, как
   * кабинет вёл себя до появления полей. Отсутствие пар вовсе тоже означает
   * преподавателя: ученик попадает в кабинет только по ссылке приглашения, и его
   * пара существует раньше, чем он впервые видит экран.
   */
  teaching: z.boolean().optional(),
  learning: z.boolean().optional(),
  /**
   * Есть ли у человека вход по паролю. По нему кабинет решает, показывать ли
   * «Сменить пароль».
   *
   * ⚠️ Это «есть личность с провайдером `password`», а НЕ «вошёл паролем прямо
   * сейчас». Разница видна на человеке с двумя привязанными входами: он мог
   * прийти через Google, и прятать от него смену пароля значило бы прятать
   * единственную дверь к паролю, которым он войдёт с другого устройства. Ничего
   * при этом не открывается: смена всё равно требует текущий пароль.
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `learning`: вкладка
   * со старым бандлом поля не пришлёт, а новый бандл на старом API его не
   * получит. Отсутствие означает «не знаем», и кабинет в этом случае пункт не
   * показывает: лишний пункт, ведущий в отказ, хуже отсутствующего.
   */
  hasPassword: z.boolean().optional(),
  /**
   * Ведёт ли человек учёт посещений и учёт оплат. Одна ручка на всех учеников
   * (решение владельца 21.08.2026).
   *
   * По ней кабинет решает, показывать ли плитки учёта в карточке ученика,
   * колонку в сетке расписания и панель отметок. Выключено у всех по умолчанию:
   * пока переключатель не тронут, об учёте не появляется ничего.
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `hasPassword`: web
   * едет отдельно от API, и вкладка со старым бандлом поле не пришлёт, а новый
   * бандл на старом API его не получит. Отсутствие означает «выключено», то
   * есть ровно то, как кабинет вёл себя до появления учёта.
   */
  tracking: z
    .object({ attendance: z.boolean(), payments: z.boolean() })
    .optional(),
  /**
   * Докуда человек дочитал список «Что нового», в миллисекундах. По нему
   * кабинет считает, сколько записей появилось ПОСЛЕ, и рисует число у пункта
   * меню.
   *
   * ⚠️ Это НЕ «когда он последний раз заходил» и не «когда входил». Вход как
   * событие у нас почти не случается: сессия живёт 60 дней без активности, а
   * `identity.last_login_at` двигается только походом к провайдеру. Здесь
   * момент последней записи, которую человек ВИДЕЛ на экране списка (разбор в
   * `docs/whats-new-design.md`).
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `hasPassword`: web
   * едет отдельно от API, и вкладка со старым бандлом поля не пришлёт, а новый
   * бандл на старом API его не получит. Отсутствие означает «не знаем», и
   * кабинет в этом случае пункта не показывает вовсе: пункт со счётчиком,
   * который посчитан неизвестно от чего, хуже отсутствующего.
   *
   * ⚠️ `null` не приходит никогда, и допущен он только ради вкладки, которая
   * разбирает ответ более старого сервера. Сервер отдаёт сюда `coalesce` с
   * датой заведения аккаунта: у нового человека «прочитано всё, что было до
   * него», иначе заведённый вчера преподаватель получил бы пачку новостей про
   * работу, которой не видел.
   */
  changelogSeenAt: z.number().int().nullable().optional(),
  /**
   * Как человека подписывать над курсором на доске. Своё значение, а не имя
   * аккаунта.
   *
   * ⚠️ Здесь приезжает СЫРОЕ значение, то есть `null` у того, кто ничего не
   * задавал. Умолчание («одно слово вместо ФИО») считает `boardNameOf` на
   * клиенте, а не сервер: правило вкусовое и меняется вместе с бандлом, а
   * колонка в базе означала бы, что старое умолчание навсегда впечаталось
   * тем, кто завёлся раньше.
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `hasPassword`: web
   * едет отдельно от API. Отсутствие и `null` означают одно и то же, «своего
   * нет», и `boardNameOf` их не различает.
   */
  boardName: BoardName.nullable().optional(),
  /**
   * Кто смотрит этот кабинет, если сессия это просмотр админом (область `view`,
   * раздел 12 docs/admin-panel-design.md). `null` у обычной сессии.
   *
   * Это единственный признак просмотра у клиента: по нему кабинет рисует полосу
   * «Просмотр кабинета: <имя>. Только чтение», выключает `y-indexeddb`, уборку
   * локальных баз досок и очередь заметок. Остальные поля тела при этом про
   * ПРЕПОДАВАТЕЛЯ: `userId`, имя, пары, учёт.
   *
   * ⚠️ Поле обязано ехать через `meBody`, иначе `MeResponse.parse` молча его
   * выбросит. Необязательное по той же причине, что `teaching` и `hasPassword`:
   * отсутствие читается как `null`, то есть «обычная сессия».
   */
  viewer: z.object({ userId: UserId, displayName: DisplayName }).nullable().optional(),
  /**
   * Какие предметы ведёт человек. По этому списку кабинет и комната прячут
   * инструменты чужих предметов: формулы у преподавателя английского,
   * пропуски в тексте у математика.
   *
   * ⚠️ `null` означает ВСЕ предметы, а не ни одного: колонка пуста у всех, кого
   * не настроили, и они обязаны видеть всё, как до появления списка. Читать
   * поле напрямую нельзя, только через `teacherSubjects` из `./subjects.ts`.
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `hasPassword`: web
   * едет отдельно от API. Отсутствие читается как `null`, то есть «все».
   *
   * ⚠️ Строки, а не `Subject`, намеренно: предмет, добавленный сервером раньше,
   * чем выкачен бандл, уронил бы строгий разбор ВСЕГО ответа, то есть сессию.
   * Незнакомые значения отбрасывает `teacherSubjects`.
   */
  subjects: z.array(z.string()).nullable().optional(),
  /**
   * Сессия НЕПОЛНАЯ: выдана ссылкой, и её набор пар не покрывает всех живых пар
   * человека как ученика (`docs/account-adopt-design.md`, 2а.6). Такой сессии
   * подпись на доске, часовой пояс и смена пароля отвечают `session_partial`, и
   * экран по этому признаку их не предлагает.
   *
   * ⚠️ Необязательное по той же причине, что `teaching` и `hasPassword`: web
   * едет отдельно от API. Отсутствие читается как `false`, то есть ровно как
   * до появления наборов.
   */
  sessionPartial: z.boolean().optional(),
  /**
   * Право заводить звонки с преподавателями действует
   * (`docs/teacher-calls-design.md`, 8.4). По нему кабинет показывает кнопку
   * звонка в разделе «Уроки».
   *
   * ⚠️ Приезжает только `true` и только у сессии держателя права без набора
   * пар (`pairScope === null`): сессии с набором, даже покрывающим все пары,
   * сторона преподавателя закрыта, и ручка создания ей
   * ответит 404. Отсутствие означает «права нет», в том числе у вкладки со
   * старым бандлом и у нового бандла на старом API.
   */
  teacherCalls: z.literal(true).optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * Одноразовый код передачи из панели в кабинет: 32 случайных байта в base64url,
 * то есть ровно 43 символа.
 */
export const ViewCode = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export type ViewCode = z.infer<typeof ViewCode>;

/**
 * Тело `POST /v1/auth/view`: обмен кода из фрагмента адреса `/view#<код>` на
 * сессию области `view`.
 *
 * Ответ это `MeResponse` (с заполненным `viewer`) БЕЗ токена: такая сессия
 * живёт только в cookie. Отказ на любую причину один, 404 `view_code_not_found`.
 */
export const ViewSignInRequest = z.object({ code: ViewCode });
export type ViewSignInRequest = z.infer<typeof ViewSignInRequest>;

/**
 * Отметка «список „Что нового“ прочитан до этой даты».
 *
 * ⚠️ Дата САМОЙ СВЕЖЕЙ ПОКАЗАННОЙ записи, а не `now()` клиента, и это не
 * придирка. Записи живут в бандле, значит вкладка, открытая неделю назад,
 * показала человеку старый список; отметив `now()`, она погасила бы записи,
 * которых он не видел, и они не вернулись бы никогда. Сервер клампит значение
 * с двух сторон (`markChangelogSeen`), но выбрать правильное может только тот,
 * кто знает, что нарисовал.
 */
export const MarkChangelogSeenRequest = z.object({
  seenAt: z.number().int().positive(),
});
export type MarkChangelogSeenRequest = z.infer<typeof MarkChangelogSeenRequest>;

/**
 * Своя подпись на доске. Пустая строка это «убрать свою и вернуть умолчание».
 */
export const UpdateBoardNameRequest = z.object({
  boardName: BoardName,
});
export type UpdateBoardNameRequest = z.infer<typeof UpdateBoardNameRequest>;

/**
 * Окончания отчеств. Список короткий намеренно: это единственная часть русского
 * ФИО, которую можно узнать по виду слова почти без ошибок.
 */
const PATRONYMIC = /(ович|евич|ьич|ич|овна|евна|ична|инична)$/;

/**
 * Окончания фамилий, и здесь список УЖЕ, чем хочется.
 *
 * В него попадают только те окончания, которыми не кончается ни одно
 * распространённое русское имя. Поэтому тут нет `-ин` и `-ина` (Валентин,
 * Константин, Мартин, Ирина, Марина, Полина, Кристина: имён с таким концом
 * больше, чем фамилий) и нет `-ян` (Демьян, Касьян).
 *
 * Нижняя граница длины нужна против коротких имён, которые кончаются
 * «фамильно»: Лев кончается на `-ев`, Ева на `-ва`. Пять букв отсекают их, а
 * фамилий короче пяти букв в списке нет.
 *
 * ⚠️ `-ко` вынесено отдельной строкой с границей в шесть букв, и это не
 * прихоть: Марко это имя из пяти букв, Приходько фамилия из девяти.
 */
const SURNAME =
  /(ов|ев|ёв|ын|ова|ева|ёва|ына|ой|ый|ский|цкий|ская|цкая|ук|юк|ых|их|дзе|швили)$/;
const SURNAME_MIN_LENGTH = 5;
const SURNAME_KO = /ко$/;
const SURNAME_KO_MIN_LENGTH = 6;

function looksLikePatronymic(word: string): boolean {
  return PATRONYMIC.test(word.toLocaleLowerCase("ru"));
}

function looksLikeSurname(word: string): boolean {
  const lower = word.toLocaleLowerCase("ru");
  if (lower.length >= SURNAME_MIN_LENGTH && SURNAME.test(lower)) return true;
  return lower.length >= SURNAME_KO_MIN_LENGTH && SURNAME_KO.test(lower);
}

/**
 * Умолчание для подписи на доске: одно слово вместо ФИО из профиля провайдера.
 *
 * Просьба преподавателя от 23.08.2026 (`docs/room-feedback-fixes.md`, раздел
 * 4): ученик видел над курсором «Приходько Владислав», потому что подпись
 * бралась из имени аккаунта как есть.
 *
 * ⚠️ **Порядок слов в русском имени не гарантирован, и мы его не угадываем.**
 * Google отдаёт то, как человек записал себя сам, а записывают и «Иван Петров»,
 * и «Петров Иван». Поэтому из трёх правил ниже первые два не зависят от порядка
 * вовсе, а там, где порядок всё-таки решает, функция НЕ выбирает.
 *
 * 1. **Отчество как якорь.** В обоих порядках имя стоит непосредственно ПЕРЕД
 *    отчеством: «Петров Иван Сергеевич» и «Иван Сергеевич Петров». Значит по
 *    найденному отчеству имя вычисляется, а не угадывается
 * 2. **Фамилия по окончанию.** Выкидываем слова, которые ею выглядят
 *    (осторожным списком выше), и если остаётся ровно одно, оно и есть имя.
 *    «Приходько Владислав» и «Владислав Приходько» дают одно и то же
 * 3. **Иначе оставляем как есть.** Ким, Це, Джон Смит: угадать нечем, и монетка
 *    здесь стоит дорого. Ошибись она, и над курсором повиснет фамилия, то есть
 *    ровно то, на что пожаловались. Человек допишет одно слово сам, поле для
 *    этого и заведено, а имя аккаунта в подписи это ровно сегодняшнее поведение
 *
 * Пустой строки не возвращает никогда: пустая подпись над курсором это не
 * «скромнее», а «непонятно, чей курсор».
 */
export function defaultBoardName(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter((p) => p !== "");
  if (parts.length === 0) return displayName.trim();
  if (parts.length === 1) return parts[0] ?? displayName.trim();

  const patronymic = parts.findIndex(looksLikePatronymic);
  // Строго больше нуля: отчество первым словом означает, что перед ним ничего
  // нет, и правило не сработало, а не что имя это последнее слово строки
  if (patronymic > 0) return parts[patronymic - 1] ?? parts[0] ?? displayName.trim();

  const rest = parts.filter((p) => !looksLikeSurname(p));
  if (rest.length === 1) return rest[0] ?? parts[0] ?? displayName.trim();

  return parts.join(" ");
}

/**
 * Чем человек подписан на доске.
 *
 * Одна функция на весь клиент по той же причине, что `isTeacherSide`: правило
 * состоит из необязательного поля и умолчания, и второе его написание однажды
 * разойдётся с первым. Здесь это особенно легко: умолчание считается ещё и в
 * окне настройки, где оно стоит подсказкой в пустом поле.
 */
export function boardNameOf(me: {
  displayName: string;
  boardName?: string | null | undefined;
}): string {
  const own = (me.boardName ?? "").trim();
  return own === "" ? defaultBoardName(me.displayName) : own;
}

/**
 * Последняя ступень подписи: когда назвать человека нечем.
 *
 * Пустой подписи над курсором не бывает: непонятно, чей это курсор. То же слово
 * стоит запасным вариантом у читателя присутствия (`presence.ts`), и второго
 * литерала для него заводить нельзя.
 */
export const SHOWN_NAME_FALLBACK = "Участник";

/**
 * Как участника подписывают на занятии: над курсором на доске и плиткой в
 * звонке. Четыре ступени, сверху вниз.
 *
 * 1. **Имя внутри группы.** Его задаёт преподаватель, и это ровно то, как
 *    ученика видят остальные в группе. Первым оно стоит потому, что двенадцать
 *    детей на одной доске это единственное место, где «Влад» и «Влад» надо
 *    различать, а различает их тот, кто их знает
 * 2. **Своя подпись на доске** (`app_user.board_name`)
 * 3. **Первое слово профильного имени** (`defaultBoardName`): не «Приходько
 *    Владислав», а «Владислав»
 * 4. **«Участник»**, когда не осталось ничего
 *
 * ⚠️ **Ступени 2 и 3 это в точности `boardNameOf`, и вызывается он, а не
 * повторяется.** Второе написание того же правила однажды разойдётся с первым, а
 * заметно это будет только тем, кто на уроке.
 *
 * ⚠️ **Считает это СЕРВЕР и отдаёт готовой строкой** (`Lesson.shownAs`, `bn` в
 * тикете доски, `name` в токене LiveKit). Первая ступень клиенту недоступна по
 * построению: имя внутри группы это чужие данные. И ровно поэтому канал LiveKit
 * нельзя обойти стороной: туда до сих пор уезжало полное профильное имя, видимое
 * всем участникам комнаты, то есть правило отменялось целиком.
 */
export function shownNameOf(who: {
  /** Имя внутри группы. `null` у занятия 1:1 и у преподавателя. */
  groupName?: string | null | undefined;
  displayName: string;
  boardName?: string | null | undefined;
}): string {
  const group = (who.groupName ?? "").trim();
  if (group !== "") return group;
  const own = boardNameOf(who).trim();
  return own === "" ? SHOWN_NAME_FALLBACK : own;
}

/**
 * Последняя ступень своего имени у сессии с набором: от пар набора не осталось
 * ни одной (ручное объединение удалило пару, сессия не отозвана). Профильное
 * здесь отдавать нельзя: его вписал преподаватель, чьего ключа на устройстве нет.
 *
 * Его же читает `selfNameSql` (`NEUTRAL_SELF_NAME` в
 * `apps/api/src/auth/session.ts`): слово одно на оба написания правила.
 */
export const SELF_NAME_FALLBACK = "Ученик";

/** Пара ученика глазами правила своего имени. */
export interface SelfNamePair {
  teacherUserId: string;
  /** `teacher_student.display_name`: `null` значит «как в профиле». */
  displayName: string | null;
  /** Преподаватель этой пары в наборе сессии. */
  inScope: boolean;
  /** Пара отвязана (`unlinked_at` задан). */
  unlinked: boolean;
  createdAt: number;
}

/**
 * Своё имя сессии, то есть как ученик назван «о себе»
 * (`docs/account-adopt-design.md`, раздел 10.1, пункт 3).
 *
 * `pairs` это ВСЕ пары ученика с пометкой, какие в наборе; `null` значит, что
 * набора у сессии нет. Порядок проверок:
 *
 * 1. набора нет: профильное;
 * 2. ни одной пары набора нет: `SELF_NAME_FALLBACK`;
 * 3. сессия полная (живых пар вне набора нет): профильное;
 * 4. в наборе есть живая пара без имени на паре, то есть пара того, кто вписал
 *    профильное: профильное;
 * 5. иначе имя на самой ранней паре набора (`createdAt`, затем преподаватель), а
 *    без имени на ней профильное.
 *
 * ⚠️ Это то же правило, что `selfNameSql` в `apps/api/src/auth/session.ts`,
 * которое сервер считает одним выражением при поиске сессии. В проде её не
 * вызывает никто: своё имя все места берут из сессии
 * (`SessionUser.displayName`). Здесь оно только для сверки: два написания
 * сравнивает `account-adopt-names.dbtest.ts`, и правка одного без другого там
 * краснеет.
 */
export function selfNameOf(pairs: readonly SelfNamePair[] | null, profileName: string): string {
  if (pairs === null) return profileName;
  const scoped = pairs.filter((p) => p.inScope);
  if (scoped.length === 0) return SELF_NAME_FALLBACK;
  if (!pairs.some((p) => !p.inScope && !p.unlinked)) return profileName;
  if (scoped.some((p) => !p.unlinked && p.displayName === null)) return profileName;
  const earliest = [...scoped].sort(
    (a, b) =>
      a.createdAt - b.createdAt ||
      (a.teacherUserId < b.teacherUserId ? -1 : a.teacherUserId > b.teacherUserId ? 1 : 0),
  )[0];
  return earliest?.displayName ?? profileName;
}

/**
 * Показывать ли человеку что-либо про учёт.
 *
 * Одна функция на весь клиент по той же причине, что `isTeacherSide`: правило
 * состоит из необязательного поля и умолчания, и второе его написание однажды
 * разойдётся с первым.
 */
export function tracksAnything(me: {
  tracking?: { attendance: boolean; payments: boolean } | undefined;
}): boolean {
  return me.tracking?.attendance === true || me.tracking?.payments === true;
}

/**
 * Показывать ли человеку разделы преподавателя.
 *
 * Одна функция на весь клиент: правило «только ученик» состоит из двух полей и
 * одного отрицания, и второе его написание однажды разойдётся с первым.
 */
export function isTeacherSide(me: {
  teaching?: boolean | undefined;
  learning?: boolean | undefined;
}): boolean {
  return !(me.learning === true && me.teaching !== true);
}

/**
 * Токен сессии возвращается в теле, а не только в cookie.
 *
 * Причина в разделе 5.7: cookie обязана быть с префиксом `__Host-`, потому что
 * в зоне висит wildcard на чужой сервер. `__Host-` запрещает атрибут Domain,
 * значит edu-api.zotov.app cookie не получит никогда. Bearer это не запасной
 * путь, а равноправный, и CSRF он не создаёт: кросс-сайтом заголовок не выставить.
 */
export const SessionResponse = MeResponse.extend({
  token: z.string().min(1),
});
export type SessionResponse = z.infer<typeof SessionResponse>;
